import styled from 'styled-components';
import { VISUALS } from '../../constants/styles';
import {
  Input,
  Inspector,
  List,
  Thumbnail,
  token,
} from '@bynder/design-system';
import { GuideType, Themeset, WIDGET_TYPE } from '../../types';
import { getTypographyWeight } from '../shared/styled-components/constants';
import { getAlphaColor } from '../shared/styled-components/Header';
import { Translate } from '@bynder/localization';
import { getWidgetIcon } from '../widgets/WidgetIcon';
import { getWidgetName } from '../../constants/widgets';
import { isDevelopment } from '../../constants/app';
import { isHomeOrSinglePage } from '../../helpers/utils';
import { addQuotes, toPx } from './Inspector/helpers';

export const StyledGuideContainer = styled.div<{
  $guideType: GuideType;
  $isHeaderHidden?: boolean;
}>`
  position: relative;
  display: flex;
  overflow: hidden;
  ${props => {
    if (isHomeOrSinglePage(props.$guideType) && props.$isHeaderHidden) {
      return `
        min-height: 100vh;
        height: 100vh;
      `;
    } else if (isHomeOrSinglePage(props.$guideType) && !props.$isHeaderHidden) {
      return `
        height: calc(100vh - ${VISUALS.BYNDER_HEADER_HEIGHT});
        min-height: calc(100vh - ${VISUALS.BYNDER_HEADER_HEIGHT});
        margin-top: ${isDevelopment ? `0px` : `108px`};
      `;
    }
    return `
      min-height: calc(100vh - ${VISUALS.HEADER_HEIGHT});
      margin-top: ${VISUALS.HEADER_HEIGHT};
      height: calc(100vh - ${VISUALS.HEADER_HEIGHT});
    `;
  }}
`;

export const StyledGuideContent = styled.div<{ $isOpen?: boolean }>`
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  flex-direction: column;
  margin-right: ${props => (props.$isOpen ? Inspector.styles.width : '0')};
  transition: margin-left 1 ease-in-out;
`;

interface StyleProps {
  theme: Themeset;
  $isHovering: boolean;
}

export const StyledChapterItemTitle = styled.div<StyleProps>(
  ({ theme, $isHovering }) => {
    const { themeset, getValueOrDefault } = theme;
    const { sideBar } = themeset || {};

    return `
  flex-grow: 1;

  color: ${
    $isHovering
      ? getValueOrDefault('sideBar.chapter.colors.text.hover.hexCode')
      : getValueOrDefault('sideBar.chapter.colors.text.normal.hexCode')
  };
  font-size: ${toPx(getValueOrDefault('sideBar.chapter.fontSize'))};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 180px;
  line-height: 125%;
  min-height: 18px;
  font-family: ${addQuotes(sideBar?.chapter?.fontFamily)};
  font-weight: ${getTypographyWeight(
    theme,
    sideBar?.chapter?.fontFamily,
    sideBar?.chapter?.fontStyle,
  )};

  `;
  },
);

export const StyledPageTitle = styled.div<{
  $isActive: boolean;
  $isDisabled: boolean;
}>(({ $isActive, $isDisabled, theme }) => {
  const { sideBar } = (theme.themeset as Themeset) || {};
  const { page } = sideBar || {};
  const { getValueOrDefault } = theme;

  const getTitleColor = () => {
    if ($isDisabled) {
      return getAlphaColor(
        0.6,
        getValueOrDefault('sideBar.page.colors.text.normal.hexCode'),
      );
    } else if ($isActive) {
      return getValueOrDefault('sideBar.page.colors.text.active.hexCode');
    }

    return getValueOrDefault('sideBar.page.colors.text.normal.hexCode');
  };

  return `
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      padding: 10px ${token.spacing3} 10px ${token.spacing4};
      line-height: 20px;
      cursor: ${$isDisabled ? 'normal' : 'pointer'};
      border-radius: ${token.radiusMedium};
      min-height: 44px;
      background-color: ${
        $isActive
          ? getValueOrDefault('sideBar.page.colors.background.active.hexCode')
          : getValueOrDefault('sideBar.backgroundColor.hexCode')
      };
      p {
        word-break: break-word;
        font-size: ${toPx(getValueOrDefault('sideBar.page.fontSize'))};
        line-height: 125%;
        flex-grow: 1;
        font-family: ${addQuotes(page?.fontFamily)};
        font-weight: ${getTypographyWeight(
          theme,
          page?.fontFamily,
          page?.fontStyle,
        )};
        color: ${getTitleColor()};
      }
      div svg {
        color: ${getAlphaColor(0.6, getTitleColor())};
      }
      &:hover {
        background-color: ${
          $isDisabled
            ? 'inherit'
            : getValueOrDefault('sideBar.page.colors.background.hover.hexCode')
        };
        p {
          color: ${
            $isDisabled
              ? getAlphaColor(
                  0.6,
                  getValueOrDefault('sideBar.page.colors.text.hover.hexCode'),
                )
              : getValueOrDefault('sideBar.page.colors.text.hover.hexCode')
          };
      }
      
        div svg {
          color: ${getAlphaColor(
            0.8,
            getValueOrDefault('sideBar.page.colors.text.hover.hexCode'),
          )};
        }
    }`;
});

export const WidgetSettingsHeader: React.FC<{
  widgetType: WIDGET_TYPE;
  subtext?: string;
}> = ({ widgetType, subtext }) => {
  return (
    <List.Item
      subtext={subtext}
      thumbnail={<Thumbnail variant="clean" icon={getWidgetIcon(widgetType)} />}
    >
      <Translate id={getWidgetName(widgetType)} />
    </List.Item>
  );
};

export const ListArrow = styled.div<{
  rotate?: number;
  isHidden?: boolean;
}>`
  visibility: ${props => (props.isHidden ? 'hidden' : 'visible')};
  cursor: pointer;
  transition: transform 0.2s;
  transform: rotate(${props => props.rotate || 0}deg);
`;

export const StyledInput = styled(Input)<{ $width?: number }>`
  width: ${props => (props.$width ? `${props.$width}px` : `120px`)};
  min-width: auto;
`;

export const StyledTabsShadow = styled.div`
  box-shadow: ${token.elevation1};
`;
